import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Link,
  Alert,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import FaxIcon from '@mui/icons-material/Print';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNotify, useDataProvider, useGetIdentity, useGetOne } from 'react-admin';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import { CustomerEventType } from '../types';
import {
  SERVICE_CANADA_ADDRESSES,
  ServiceCanadaAddress,
  getDefaultServiceCanadaAddress
} from './serviceCanadaAddresses';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

// Derive a human-readable form name from a signed CPP document URL. The signed
// filename is set deterministically upstream (CppBenefitsSigningService →
// `CPP_DeathBenefit` / `CPP_SurvivorBenefits`) and survives into the storage
// path, so we can classify by inspecting the URL.
const labelForCppUrl = (url: string): string => {
  let name = url;
  try {
    name = decodeURIComponent(new URL(url).pathname);
  } catch {
    name = decodeURIComponent(url);
  }
  const lower = name.toLowerCase();
  if (lower.includes('death') && lower.includes('benefit')) {
    return "CPP Death Benefit (ISP-1200)";
  }
  if (lower.includes('survivor')) {
    return "CPP Survivor's Pension (ISP-1300)";
  }
  return 'CPP Benefits document';
};

interface VerifiedDoc {
  id: string;
  verification_id: string;
  document_type: string;
  status: string;
  issued_at: string;
}

interface CppBenefitsMailingModalProps {
  open: boolean;
  onClose: () => void;
  funeralUuid: string;
  funeralId: number;
  deceasedName: string;
  deceasedProvince?: string;
  deceasedPostalCode?: string;
  deceasedAddress?: string;
  buyerName: string;
  buyerRelationship?: string;
  onSuccess: () => void;
}

// "Send to Service Canada" window. Combines the two delivery channels:
//   • CPP Benefits (ISP-1200 / ISP-1300) → physically mailed via Canada Post
//     (PostGrid) to the selected Service Canada office.
//   • OAS Cancellation (ISP-1201) → faxed via Dropbox Sign to the national
//     Service Canada fax number (configured server-side).
// Each section only appears when its signed document exists, so an Ontario
// funeral with both shows both; the agent sends each independently.
const CppBenefitsMailingModal: React.FC<CppBenefitsMailingModalProps> = ({
  open,
  onClose,
  funeralUuid,
  funeralId,
  deceasedName,
  deceasedProvince,
  deceasedPostalCode,
  deceasedAddress,
  buyerName,
  buyerRelationship,
  onSuccess
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const [mailing, setMailing] = useState(false);
  const [faxing, setFaxing] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<ServiceCanadaAddress | null>(null);
  // URLs the agent has checked for mailing. Defaults to every signed CPP doc.
  const [selectedCppUrls, setSelectedCppUrls] = useState<string[]>([]);
  // The Cleo Contract Receipt(s) (stored funeral_documents — one per paid
  // contract), offered as optional enclosures. Not checked by default — the
  // agent opts in.
  const [receipts, setReceipts] = useState<{ url: string; label: string }[]>([]);
  // The case's current Proof of Death. Service Canada returns a CPP application
  // that arrives without it, so it's enclosed by default when one exists — and
  // when none has been issued we warn instead of silently mailing without it.
  const [podDoc, setPodDoc] = useState<VerifiedDoc | null>(null);
  const [podLoaded, setPodLoaded] = useState(false);
  const [includePod, setIncludePod] = useState(true);

  useEffect(() => {
    if (!open || !funeralId) {
      setReceipts([]);
      setPodDoc(null);
      setPodLoaded(false);
      return;
    }
    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${PAPERMAN_API_URL}documents/funeral/${funeralId}/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const receiptDocs = (data.funeral_documents || []).filter(
          (d: any) => d.document_type === 'cleo_contract_receipt' && d.public_url,
        );
        // A second paid contract's receipt carries its type + id in the filename
        // ("Cleo Contract Receipt - 743 - Pre-need 605"); a lone one keeps the plain label.
        setReceipts(receiptDocs.map((d: any) => ({
          url: d.public_url,
          label: receiptDocs.length > 1 ? (d.original_filename || 'Cleo Contract Receipt') : 'Cleo Contract Receipt',
        })));
        // `verified_documents` comes back newest-first, so the first valid POD
        // is the current one — the same one the server encloses.
        const pod = (data.verified_documents || []).find(
          (d: any) => d.document_type === 'POD' && d.status === 'valid',
        );
        setPodDoc(pod || null);
        setIncludePod(!!pod);
      } catch {
        /* best-effort — the receipt option just won't appear */
      } finally {
        setPodLoaded(true);
      }
    })();
  }, [open, funeralId]);

  // Open the POD PDF in a new tab. It lives in a private bucket behind an
  // authenticated route, so it can't be a plain href like the other previews.
  const handlePreviewPod = async () => {
    if (!podDoc) return;
    try {
      const token = await getToken();
      const res = await fetch(`${PAPERMAN_API_URL}documents/${podDoc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load Proof of Death');
      window.open(URL.createObjectURL(await res.blob()), '_blank');
    } catch {
      notify('Failed to open the Proof of Death', { type: 'error' });
    }
  };

  // Pull the funeral so we know which channels apply and their current status.
  const { data: funeral, refetch } = useGetOne(
    'funerals',
    { id: funeralId },
    { enabled: open && !!funeralId },
  );

  const cppSignedUrls: string[] = Array.isArray((funeral as any)?.cpp_benefits_signed_urls)
    ? (funeral as any).cpp_benefits_signed_urls
    : [];
  const hasCpp = cppSignedUrls.length > 0;

  // Documents offered in the "Documents to mail" list: the signed CPP forms plus
  // the optional Cleo Contract Receipt(s).
  const mailableDocs: { url: string; label: string }[] = [
    ...cppSignedUrls.map((u) => ({ url: u, label: labelForCppUrl(u) })),
    ...receipts,
  ];
  const hasOas = Array.isArray((funeral as any)?.oas_cancellation_signed_urls)
    && (funeral as any).oas_cancellation_signed_urls.length > 0;
  const cppMailed = (funeral as any)?.cpp_benefits_mailing_status === 'sent';
  const oasFaxed = (funeral as any)?.oas_cancellation_fax_status === 'sent';

  // Set default Service Canada office when modal opens
  useEffect(() => {
    if (open && !selectedAddress) {
      const defaultAddress = getDefaultServiceCanadaAddress(deceasedProvince, deceasedPostalCode);
      setSelectedAddress(defaultAddress);
    }
  }, [open, deceasedProvince, deceasedPostalCode, selectedAddress]);

  // Default every signed CPP document to checked once the funeral loads (and
  // re-sync if a new form gets signed while the window is open).
  const cppUrlsKey = cppSignedUrls.join('|');
  useEffect(() => {
    if (open) {
      setSelectedCppUrls(cppSignedUrls);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cppUrlsKey]);

  const toggleCppUrl = (url: string) => {
    setSelectedCppUrls((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url],
    );
  };

  const handleSendMail = async () => {
    if (!selectedAddress) {
      notify('Please select a Service Canada office', { type: 'warning' });
      return;
    }

    if (selectedCppUrls.length === 0) {
      notify('Please select at least one document to mail', { type: 'warning' });
      return;
    }

    setMailing(true);
    try {
      const token = await getToken();
      const response = await fetch(`${PAPERMAN_API_URL}contract/send-cpp-benefits-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Refresh-Token': await getRefreshToken(),
        },
        body: JSON.stringify({
          uuid: funeralUuid,
          documentUrls: selectedCppUrls,
          includePod: includePod && !!podDoc,
          recipientAddress: {
            name: selectedAddress.name,
            address_line1: selectedAddress.address_line1,
            city: selectedAddress.city,
            province_code: selectedAddress.province_code,
            postal_code: selectedAddress.postal_code,
            country: selectedAddress.country
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to mail CPP Benefits document');
      }

      try {
        await dataProvider.create('customer_events', {
          data: {
            funeral_id: funeralId,
            type: CustomerEventType.CPP_BENEFITS_MAILED,
            timestamp: new Date().toISOString(),
            user_id: identity?.id ? Number(identity.id) : undefined,
            user_name: identity?.fullName || 'System',
            message: `CPP Benefits document mailed to ${selectedAddress.name}`,
            recipient_name: selectedAddress.name,
            contact_detail: `${selectedAddress.address_line1}, ${selectedAddress.city}, ${selectedAddress.province_code} ${selectedAddress.postal_code}`
          }
        });
      } catch (eventError) {
        console.error('Error creating customer event:', eventError);
      }

      notify('CPP Benefits document has been mailed successfully', { type: 'success' });
      void refetch?.();
      onSuccess();
      // Keep the window open if an OAS fax still needs sending.
      if (!hasOas || oasFaxed) onClose();
    } catch (error: any) {
      console.error('Error mailing CPP Benefits document:', error);
      notify(error.message || 'Failed to mail CPP Benefits document', { type: 'error' });
    } finally {
      setMailing(false);
    }
  };

  const handleSendFax = async () => {
    setFaxing(true);
    try {
      const token = await getToken();
      // Faxes via the HelloFax email-to-fax gateway (no Dropbox Sign API plan).
      const response = await fetch(`${PAPERMAN_API_URL}oas-cancellation/fax/${funeralId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Refresh-Token': await getRefreshToken(),
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fax OAS cancellation document');
      }

      notify('OAS cancellation form has been faxed to Service Canada', { type: 'success' });
      void refetch?.();
      onSuccess();
      if (!hasCpp || cppMailed) onClose();
    } catch (error: any) {
      console.error('Error faxing OAS cancellation document:', error);
      notify(error.message || 'Failed to fax OAS cancellation document', { type: 'error' });
    } finally {
      setFaxing(false);
    }
  };

  const busy = mailing || faxing;
  const handleClose = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Send to Service Canada</DialogTitle>
      <DialogContent>
        {/* Summary Section */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Document Summary
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Deceased:</strong> {deceasedName}
            </Typography>
          </Box>
          {deceasedAddress && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Address:</strong> {deceasedAddress}
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Applicant:</strong> {buyerName}
            </Typography>
          </Box>
          {buyerRelationship && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Relationship:</strong> {buyerRelationship}
              </Typography>
            </Box>
          )}
        </Box>

        {/* CPP Benefits — physical mail */}
        {hasCpp && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MailOutlineIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                CPP Benefits — Mailed via Canada Post
              </Typography>
              {cppMailed && <Chip size="small" color="success" label="Mailed" />}
            </Box>

            {/* Documents that will be enclosed in the letter. Checked by
                default; the agent can deselect any and preview each. */}
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Documents to mail
              </Typography>
              <FormGroup>
                {mailableDocs.map(({ url, label }, idx) => (
                  <Box
                    key={`${url}-${idx}`}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedCppUrls.includes(url)}
                          onChange={() => toggleCppUrl(url)}
                          disabled={busy}
                        />
                      }
                      label={<Typography variant="body2">{label}</Typography>}
                    />
                    <Link
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem' }}
                    >
                      Preview
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </Box>
                ))}
                {podDoc && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={includePod}
                          onChange={() => setIncludePod((v) => !v)}
                          disabled={busy}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Proof of Death{' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            ({podDoc.verification_id})
                          </Typography>
                        </Typography>
                      }
                    />
                    <Link
                      component="button"
                      type="button"
                      onClick={() => { void handlePreviewPod(); }}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem' }}
                    >
                      Preview
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </Box>
                )}
              </FormGroup>
              {podLoaded && !podDoc && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No Proof of Death has been issued for this case. Service Canada needs it
                  with the application — issue it from the Documents tab, then re-open this
                  window so it can be enclosed.
                </Alert>
              )}
            </Box>

            <FormControl fullWidth sx={{ mb: 1 }}>
              <InputLabel id="service-canada-office-label">Service Canada Office</InputLabel>
              <Select
                labelId="service-canada-office-label"
                value={selectedAddress?.id || ''}
                label="Service Canada Office"
                onChange={(e) => {
                  const address = SERVICE_CANADA_ADDRESSES.find(addr => addr.id === e.target.value);
                  setSelectedAddress(address || null);
                }}
                disabled={busy}
              >
                {SERVICE_CANADA_ADDRESSES.map((address) => (
                  <MenuItem key={address.id} value={address.id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {address.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {address.address_line1}, {address.city}, {address.province_code} {address.postal_code}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedAddress && (
              <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Mailing To:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{selectedAddress.name}</Typography>
                <Typography variant="body2">{selectedAddress.address_line1}</Typography>
                <Typography variant="body2">
                  {selectedAddress.city}, {selectedAddress.province_code} {selectedAddress.postal_code}
                </Typography>
                <Typography variant="body2">{selectedAddress.country}</Typography>
              </Box>
            )}

            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={handleSendMail}
                variant="contained"
                color="primary"
                startIcon={mailing ? <CircularProgress size={16} color="inherit" /> : <MailOutlineIcon />}
                disabled={busy || !selectedAddress || selectedCppUrls.length === 0}
              >
                {cppMailed ? 'Re-send by Mail' : 'Send by Mail'}
              </Button>
            </Box>
          </Box>
        )}

        {hasCpp && hasOas && <Divider sx={{ my: 2 }} />}

        {/* OAS Cancellation — fax */}
        {hasOas && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FaxIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                OAS Cancellation — Faxed to Service Canada
              </Typography>
              {oasFaxed && <Chip size="small" color="success" label="Faxed" />}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              The signed Notification of Death (ISP-1201) will be faxed to Service Canada
              via Dropbox Sign.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={handleSendFax}
                variant="contained"
                color="primary"
                startIcon={faxing ? <CircularProgress size={16} color="inherit" /> : <FaxIcon />}
                disabled={busy}
              >
                {oasFaxed ? 'Re-send by Fax' : 'Send by Fax'}
              </Button>
            </Box>
          </Box>
        )}

        {!hasCpp && !hasOas && (
          <Typography variant="body2" color="text.secondary">
            No signed CPP Benefits or OAS Cancellation document is available to send yet.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CppBenefitsMailingModal;
